import { supabase } from '../../lib/supabase'
import type { RosterRecord } from './rosterFigures'

type PlacementRow = {
  employee_id: string
  team_id: string
  title: string
  teams: { name: string } | null
  shift_templates: { name: string } | null
  employees: { display_name: string; employee_identifier: string; seniority_date: string } | null
}

function todayIso(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

/**
 * Every employee the signed-in account can see, on the placement in effect
 * today, with the qualifications held today and whether a detail is under
 * way. Row-level security limits each table to the caller's own teams.
 */
export async function getRosterRecords(): Promise<RosterRecord[]> {
  const today = todayIso()
  const { data: placements, error } = await supabase
    .from('employee_assignments')
    .select('employee_id, team_id, title, teams(name), shift_templates(name), employees(display_name, employee_identifier, seniority_date)')
    .eq('active', true)
    .lte('effective_from', today)
    .or(`effective_to.is.null,effective_to.gte.${today}`)
  if (error) throw new Error('The seniority roster could not be loaded.')
  const rows = (placements ?? []) as unknown as PlacementRow[]
  if (rows.length === 0) return []

  const employeeIds = rows.map((row) => row.employee_id)
  const [qualificationResult, detailResult] = await Promise.all([
    supabase
      .from('employee_qualifications')
      .select('employee_id, qualifications(name)')
      .in('employee_id', employeeIds)
      .lte('effective_from', today)
      .or(`effective_to.is.null,effective_to.gte.${today}`),
    supabase.from('details').select('employee_id').in('employee_id', employeeIds).lte('start_date', today).gte('end_date', today),
  ])
  if (qualificationResult.error || detailResult.error) throw new Error('The seniority roster could not be loaded.')

  const qualifications = new Map<string, string[]>()
  for (const row of (qualificationResult.data ?? []) as unknown as { employee_id: string; qualifications: { name: string } | null }[]) {
    if (row.qualifications) qualifications.set(row.employee_id, [...(qualifications.get(row.employee_id) ?? []), row.qualifications.name])
  }
  const detailed = new Set(((detailResult.data ?? []) as { employee_id: string }[]).map((row) => row.employee_id))

  return rows.flatMap((row) =>
    row.employees && row.teams
      ? [
          {
            employeeId: row.employee_id,
            identifier: row.employees.employee_identifier,
            employee: row.employees.display_name,
            seniorityDate: row.employees.seniority_date,
            teamId: row.team_id,
            team: row.teams.name,
            title: row.title,
            shift: row.shift_templates?.name ?? 'No shift',
            qualifications: (qualifications.get(row.employee_id) ?? []).sort(),
            status: detailed.has(row.employee_id) ? ('detailed' as const) : ('active' as const),
          },
        ]
      : [],
  )
}
